import { Face } from "@spi/Common/Geometry";
import { ILogger } from "../Logging/Logger";
import { BoundingBox } from '@spi/Common/Util';
import { ConditionalGate } from "./ConditionalGate";
import { ConvexShape } from "./ConvexShape";
import { HeightSpan } from "./HeightSpan";
import { NavigationMeshSegment } from "./NavigationMeshSegment";
import { PathNode } from "./PathNode";

export class NavigationMesh {

    private segmentCount: number;
    private faceLists: Face[][];
    private conditionalGates: ConditionalGate[];
    public pathNodes: PathNode[];

    constructor(public settings: NavigationMeshSettings, public logger: ILogger) {
        this.segmentCount = 0;
        this.faceLists = [];
        this.conditionalGates = [];
    }

    public addGeometry(faces: Face[]) {
        this.faceLists.push(faces);
    }

    public addConditionalGates(gates: ConditionalGate[]) {
        this.conditionalGates.push(...gates);
    }

    private createSegments() {
        let min = this.settings.bounds.min;
        let max = this.settings.bounds.max;

        let size = this.settings.segmentSize;

        let segments: NavigationMeshSegment[][] = [];

        for (let x = min[0], xc = 0; x < max[0]; x += size, xc++) {
            segments.push([]);
            for (let y = min[1], yc = 0; y < max[1]; y += size, yc++) {
                let segment = new NavigationMeshSegment(
                    this,
                    this.settings,
                    this.logger,
                    new BoundingBox([x, y, min[2]], [x + size, y + size, max[2]]),
                    [xc, yc]
                );

                this.segmentCount++;

                segments[xc].push(segment);
            }
        }

        for (let x = 0; x < segments.length; x++) {
            for (let y = 0; y < segments[x].length; y++) {
                let segment = segments[x][y];
                let neighbours = this.getSegmentNeighbours(segment, segments);

                for (let neighbour of neighbours) {
                    segment.addNeighbour(neighbour);
                }
            }
        }

        return segments;
    }

    private handleSegment(segment: NavigationMeshSegment, segments: NavigationMeshSegment[][]) {

        for (let list of this.faceLists) {
            segment.heightField.voxelizeGeometry(...list);
        }

        segment.heightField.voxelizeConditionalGates(...this.conditionalGates);

        segment.heightField.generate();

        let spans: HeightSpan[] = [];

        segment.heightField.openSpanMap.forEach(spanList => spans.push(...spanList));

        spans.sort((a, b) => a.bottom - b.bottom);

        segment.usedSpans = new Map<HeightSpan, ConvexShape>();

        for (let neighbour of this.getSegmentNeighbours(segment, segments)) {

            // if a neighbour is not yet processed we can skip it, it will feed of our data instead of the other way around
            if (!neighbour.processed) {
                neighbour.neighbourVisited(segment);
                continue;
            }

            let map = new Map<string, HeightSpan[]>();

            // ensure that shapes of neighbouring segments can be connected to other segments
            neighbour.edges.forEach((shape, span) => {

                // ensure that neighbours of spans can be located
                let key = `${span.spanLocation[0]},${span.spanLocation[1]}`;
                if (map.has(key)) {
                    map.get(key).push(span);
                } else {
                    map.set(key, [span]);
                }

                segment.usedSpans.set(span, shape);
            });
            let edgeSpans = segment.getEdgeSpans(spans);

            for (let span of edgeSpans) {
                span.attachExtraNeighbours(map, this.settings.maxNeighbourStepHeight);
            }

            neighbour.neighbourVisited(segment);
        }

        segment.generateShapes(spans);
        segment.findShapeNeighbours();
        segment.generatePathNodes();

        segment.done();
    }

    private getSegmentNeighbours(source: NavigationMeshSegment, segments: NavigationMeshSegment[][]): NavigationMeshSegment[] {
        let neighbours: NavigationMeshSegment[] = [];

        let offsets = [
            [-1, 0],
            [1, 0],
            [0, -1],
            [0, 1]
        ];

        let index = source.index;
        for (let offset of offsets) {
            let segment = this.sampleSegment(index[0] + offset[0], index[1] + offset[1], segments);
            if (segment) {
                neighbours.push(segment);
            }
        }

        return neighbours;
    }

    private sampleSegment(x: number, y: number, segments: NavigationMeshSegment[][]): NavigationMeshSegment {
        if (x < 0 || x >= segments.length) {
            return null;
        }
        if (y < 0 || y >= segments[x].length) {
            return null;
        }

        return segments[x][y];
    }

    public generate() {
        this.pathNodes = [];

        let segments = this.createSegments();

        let count = 0;
        for (let x = 0; x < segments.length; x++) {
            for (let y = 0; y < segments[x].length; y++) {
                let segment = segments[x][y];

                count++;
                this.handleSegment(segment, segments);
                this.pathNodes.push(...segment.pathNodes); // also include nodes without neighbours, they are not required for the graph but for picking (to avoid clicks going through areas).

                this.logger.progress(`Processed ${count}/${this.segmentCount} map segments of ${this.settings.segmentSize}x${this.settings.segmentSize}`);
            }
        }

        this.logger.clear();
        this.logger.info('Done');
    }

}

export type NavigationMeshSettings = {
    voxelSize: number, // size of the voxels
    segmentSize: number, // size of the segments of map which are processed sequentially
    bounds: BoundingBox, // the bounds of the area for which a navigation mesh has to be created
    maxConvexExpansion: number, // the maximum expansion of a convex shape in each direction
    minTraversableHeight: number, // the minimum height between two spans to consider the lower one traversable
    maxNeighbourStepHeight: number // the maximum height difference to determine whether stepping to a neighbour is possible
}